import React, { useState, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { useNavigate } from 'react-router-dom'; // Add for back button
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { useAuth } from '../AuthContext';

function UpdateTiers() {
  const [tiersData, setTiersData] = useState([]);
  const { csrfToken, fetchCsrfToken, user } = useAuth();
  const [gridApi, setGridApi] = useState(null);
  const navigate = useNavigate(); // For back button

  useEffect(() => {
    const fetchTiersWork = async () => {
      if (!csrfToken) await fetchCsrfToken();
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/supabase/get_tiers_work`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'x-csrf-token': csrfToken },
          body: JSON.stringify({ user_id: user.id }),  
          credentials: 'include',
        });
        if (!response.ok) throw new Error('Fetch failed');
        const data = await response.json();
        console.log('Raw RPC data:', data);
        console.log('First row keys:', data[0] ? Object.keys(data[0]) : 'Empty');
        setTiersData(data);
        console.log('Fetched tiers_work:', data);
      } catch (error) {
        console.error('Error fetching tiers_work:', error);
      }
    };
    fetchTiersWork();
  }, [csrfToken, fetchCsrfToken, user]);

  const onGridReady = (params) => {
    setGridApi(params.api);
  };

const handleCellEdit = async (params) => {
  const updatedRow = params.data;
  const { created_at, ...updateData } = updatedRow;
  const requestBody = { ...updateData, user_id: user.id };
  console.log('Update body keys:', Object.keys(requestBody));  // Log order
  console.log('Body JSON:', JSON.stringify(requestBody));  // Log full body
  try {
    const response = await fetch(`${process.env.REACT_APP_API_URL}/supabase/update_tiers_work`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-csrf-token': csrfToken },
      body: JSON.stringify(requestBody),  // Use var
      credentials: 'include',
    });
    if (!response.ok) {
      const err = await response.json();
      console.error('RPC error:', err);
      throw new Error('Update failed');
    }
    // Refresh
    const fetchResponse = await fetch(`${process.env.REACT_APP_API_URL}/supabase/get_tiers_work`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-csrf-token': csrfToken },
      body: JSON.stringify({ user_id: user.id }),
      credentials: 'include',
    });
    const newData = await fetchResponse.json();
    setTiersData(newData);
  } catch (error) {
    console.error('Error updating tiers_work:', error);
  }
};

  const handleAddTier = async () => {
    // Placeholder for new row; prompt or use form for inputs
    const newTier = { 
      tier: 'New Tier', 
      max_sales: 10, 
      max_images_uploads: 250, 
      image_credits: 250, 
      cost_per_image: 0.20, 
      stripe_price_id: '', 
      is_membership: true, 
      description: '',
      // No old fields: balance_credit_amount, cost_per_inventory_item, min_balance_for_uploads
    };
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/supabase/insert_tiers_work`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-csrf-token': csrfToken },
        body: JSON.stringify({ ...newTier, user_id: user.id }),
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Insert failed');
      // Refresh
      const fetchResponse = await fetch(`${process.env.REACT_APP_API_URL}/supabase/get_tiers_work`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-csrf-token': csrfToken },
        body: JSON.stringify({ user_id: user.id }),
        credentials: 'include',
      });
      const newData = await fetchResponse.json();
      setTiersData(newData);
    } catch (error) {
      console.error('Error inserting tiers_work:', error);
    }
  };

  const handleSync = async () => {
    if (!window.confirm('Sync changes to production tiers? This overwrites live data.')) return;
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/supabase/sync_tiers_work_to_tiers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-csrf-token': csrfToken },
        body: JSON.stringify({ user_id: user.id }),
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Sync failed');
      alert('Synced successfully');
    } catch (error) {
      console.error('Error syncing tiers_work:', error);
    }
  };

  // Updated columns for new structure (dropped old fields)
  const columns = [
    { headerName: 'Tier', field: 'tier', editable: true },
    { headerName: 'Max Sales', field: 'max_sales', editable: true },
    { headerName: 'Max Uploads', field: 'max_images_uploads', editable: true },
    { headerName: 'Image Credits', field: 'image_credits', editable: true },  // New: Starting pool
    { headerName: 'Cost per Image', field: 'cost_per_image', editable: true },  // New: Bill rate
    { headerName: 'Stripe Price ID', field: 'stripe_price_id', editable: true },  // New: Sandbox IDs
    { headerName: 'Is Membership', field: 'is_membership', editable: true },  // New: Sub vs. top-up
    { headerName: 'Description', field: 'description', editable: true },
    // Dropped: balance_credit_amount, cost_per_inventory_item, min_balance_for_uploads
  ];

  return (
    <div className="p-4">
      <button
        onClick={() => navigate('/admin')}
        className="mb-4 bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
      >
        Back to Admin
      </button>
      <h2 className="text-xl mb-2">Update Tiers (Work Table)</h2>
      <div className="mb-4 flex space-x-4">
        <button onClick={handleAddTier} className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600">
          Add New Tier
        </button>
        <button onClick={handleSync} className="bg-yellow-500 text-white px-4 py-2 rounded hover:bg-yellow-600">
          Sync to Production
        </button>
      </div>
     <div className="ag-theme-alpine max-h-[80vh] overflow-auto">  {/* Tailwind: Max 80% viewport, scroll if huge */}
  <AgGridReact
    rowData={tiersData}
    columnDefs={columns}
    onGridReady={onGridReady}
    onCellValueChanged={handleCellEdit}
    pagination={false}  // Disable for full view (re-enable if >50 rows)
    domLayout="autoHeight"  // Expands to fit all rows (no fixed height/scroll)
    suppressPaginationPanel={true}  // Hide "1 of 24" if pagination=false
    rowHeight={40}  // Optional: Tweak for better fit (default 28px too tight)
  />
</div>
    </div>
  );
}

export default UpdateTiers;